/**
 * Ramadan gift tags — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_ramadan_eid`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const ramadanGiftTagsPack: ToilePack = {
    slug: 'ramadan-gift-tags',
    category: 'season',
    name: 'Ramadan gift tags',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'kareem', label: 'Ramadan Kareem', subject: 'a sheet of six rectangular tags in a two-by-three grid, each lettered "Ramadan Kareem" with a small crescent, a punch hole and a blank "To" line'},
        {key: 'dates', label: 'Date box', subject: 'a sheet of round tags lettered "A little something for your iftar" with a drawing of three dates on a leaf, a blank name line under each'},
        {key: 'lantern', label: 'Lantern shape', subject: 'a sheet of lantern-shaped tags with dashed cut outlines, each lettered "Ramadan Mubarak" across the body of the lantern'},
    ],
    styles: [
        {key: 'outline', label: 'Outline', style: 'bold black outlines on empty shapes, the drawings left to colour in', palette: 'black ink only', framing: 'The pieces spaced apart so each can be cut cleanly'},
        {key: 'geometric', label: 'Geometric', style: 'flat geometric print of eight-pointed stars and crescents with solid colour fills and crisp edges', palette: 'teal, gold and white', framing: 'The coloured pieces spaced apart on the white page'},
        {key: 'pastel', label: 'Pastel', style: 'soft flat illustration with rounded shapes and a gentle dotted border on every piece', palette: 'mint, blush, butter yellow and lavender', framing: 'The pieces spaced apart on the white page'},
    ],
    directions: ['dates box', 'teacher gift', 'gold stars'],
    directionHint: 'try: dates box',
    seo: {
        title: 'Ramadan gift tags printable: Kareem, date box, lantern',
        metaDescription: 'Nine printable Ramadan gift tags for dates and sweets: rectangles lettered Ramadan Kareem, round date tags and lantern shapes.',
        h1: 'Ramadan gift tags: Ramadan Kareem, date box, lantern shape',
        tagline: 'Nine print-and-cut sheets, three tag shapes each in outline, geometric and pastel finishes.',
        intro: 'Print-and-cut tags for dates, sweets or iftar boxes sent to neighbours during the month. Rectangular tags are lettered Ramadan Kareem with a small crescent, a punch hole and a blank To line. Round tags say A little something for your iftar, over three dates on a leaf. Lantern-shaped tags read Ramadan Mubarak across the body. Type a direction such as dates box and the tag is drawn with it.',
        useCases: [
            'a box of dates for neighbours',
            'an iftar box for colleagues',
            "a teacher's gift near the end of the month",
            'a sweet box shared with family',
        ],
        faq: [
            {q: 'How is a rectangular tag attached?', a: 'It has a punch hole for string, and the name goes on the blank line.'},
            {q: 'What are the three tag shapes?', a: 'Rectangles lettered Ramadan Kareem, round date-box tags, and lantern-shaped tags lettered Ramadan Mubarak.'},
            {q: 'Do the greeting and shapes change between styles?', a: 'No. The greeting and the shapes stay the same in each style; only the finish changes.'},
            {q: 'Can I ask for a different greeting?', a: 'Yes. Type a direction such as gold stars and the tags are drawn with it. That is a request, not a promised result.'},
        ],
    },
}
